import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { api, fmt, stateBadgeStyleObj } from "../../../services/erpApi";
import { get, post } from "../../../services/lewooApi";
import { LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import {
  Plus, Search, Eye, Pencil, Trash2, Building2, User,
  FileText, ShoppingCart, Download, Mail, MessageCircle, Clock
} from "lucide-react";

const PAGE_SIZE = 100;

const norm = (r) => (r && typeof r === "object" && !Array.isArray(r) && "data" in r ? r.data : r);
const rowsOf = (res) => {
  const n = norm(res);
  if (Array.isArray(n)) return { rows: n, total: null };
  if (Array.isArray(n?.data)) return { rows: n.data, total: typeof n.total === "number" ? n.total : null };
  return { rows: [], total: null };
};

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
  fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
};
const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
const iconBtn = (color) => ({
  background: "#fff", border: "1px solid #e2e7e2", borderRadius: 6, padding: "5px 7px",
  cursor: "pointer", color, display: "inline-flex", alignItems: "center",
});
const stylesOdooButton = { border: "1px solid #d8dde2", padding: "6px 11px", cursor: "pointer", fontSize: 11, background: "#fff" };
const chatterTabStyle = { border: 0, borderRight: "1px solid #dfe4e7", background: "#fff", color: "#63758a", padding: "8px 13px", fontSize: 11 };
const OdooDetailField = ({ label, children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "128px 1fr", minHeight: 34, alignItems: "center", borderBottom: "1px solid #edf0f2", fontSize: 11 }}>
    <strong style={{ color: "#263746", fontWeight: 600 }}>{label}</strong>
    <span style={{ color: "#63758a", minHeight: 14 }}>{children || ""}</span>
  </div>
);

const FormField = ({ label, children, required }) => (
  <div style={{ marginBottom: "14px" }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: "#be123c" }}> *</span>}</label>
    {children}
  </div>
);

const DetailField = ({ label, children, full }) => (
  <div style={{ background: "#fafbfa", border: "1px solid #f1f3f0", borderRadius: 8, padding: "10px 12px", gridColumn: full ? "1 / -1" : undefined }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".02em", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13.5, color: "#1e293b", fontWeight: 500, lineHeight: 1.5, wordBreak: "break-word" }}>{children ?? "—"}</div>
  </div>
);

const chip = (text, bg, color) => (
  <span style={{ background: bg, color, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{text}</span>
);

const EMPTY_FORM = {
  name: "", display_name: "", company_name: "", is_company: true,
  email: "", phone: "", mobile: "", street: "", city: "", vat: "",
  customer: true, supplier: false, is_active: true,
};

const AdminPanelPartnersPage = () => {
  const toast = useToast();
  const permissions = useSelector((s) => s.auth?.permissions);
  const canManage = !!(permissions?.canManageCRM || permissions?.canManageCrm || permissions?.isAdmin);

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasServerTotal, setHasServerTotal] = useState(false);
  const knownTotal = hasServerTotal;
  const totalPages = knownTotal ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const nums = [1];
    if (page > 3) nums.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) nums.push(i);
    if (page < totalPages - 2) nums.push("…");
    nums.push(totalPages);
    return nums;
  }, [totalPages, page]);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLeads, setDetailLeads] = useState(null);
  const [detailOrders, setDetailOrders] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [mailOpen, setMailOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [mailTemplateId, setMailTemplateId] = useState("");
  const [waTemplateId, setWaTemplateId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteProducts, setQuoteProducts] = useState([]);
  const [quoteLines, setQuoteLines] = useState([]);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteBusy, setQuoteBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [typeFilter, roleFilter, activeFilter]);
  useEffect(() => { setSelectedIds([]); }, [search, typeFilter, roleFilter, activeFilter, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", String(PAGE_SIZE));
      if (search) qs.set("search", search);
      if (typeFilter === "company") qs.set("is_company", "true");
      if (typeFilter === "individual") qs.set("is_company", "false");
      if (roleFilter === "customer") qs.set("customer_rank", "1");
      if (roleFilter === "supplier") qs.set("supplier_rank", "1");
      if (activeFilter === "active") qs.set("is_active", "true");
      if (activeFilter === "inactive") qs.set("is_active", "false");
      if (activeFilter === "all") qs.set("is_active", "all");
      const res = await get(`/api/partners?${qs.toString()}`);
      let r = Array.isArray(res?.data) ? res.data : [];
      if (roleFilter === "customer") r = r.filter((p) => Number(p.customer_rank) > 0);
      if (roleFilter === "supplier") r = r.filter((p) => Number(p.supplier_rank) > 0);
      setRows(r);
      const t = typeof res?.total === "number" ? res.total : null;
      setTotal(t != null ? t : r.length);
      setHasServerTotal(t != null && t > 0);
    } catch (e) {
      toast(e.message || "Failed to load customers", "error");
      setRows([]);
    }
    setLoading(false);
    setLoaded(true);
  }, [search, typeFilter, roleFilter, page, toast]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (format = "xlsx") => {
    try {
      const exportParams = {};
      if (search) exportParams.search = search;
    if (typeFilter === "company") exportParams.is_company = true;
    if (typeFilter === "individual") exportParams.is_company = false;
    if (roleFilter === "customer") exportParams.customer_rank = 1;
    if (roleFilter === "supplier") exportParams.supplier_rank = 1;
    if (activeFilter === "active") exportParams.is_active = true;
    if (activeFilter === "inactive") exportParams.is_active = false;
    if (activeFilter === "all") exportParams.is_active = "all";
      exportParams.format = format;
      await api.partners.export(exportParams);
      toast("Export téléchargé", "success");
    } catch (e) {
      try {
        // fallback CSV client-side: fetch all with pagination
        const all = [];
        let pageNo = 1;
        const limit = 500;
        for (let guard = 0; guard < 20; guard++) {
          const qs = new URLSearchParams();
          qs.set("page", String(pageNo));
          qs.set("limit", String(limit));
          if (search) qs.set("search", search);
          if (typeFilter === "company") qs.set("is_company", "true");
          if (typeFilter === "individual") qs.set("is_company", "false");
          if (roleFilter === "customer") qs.set("customer_rank", "1");
          if (roleFilter === "supplier") qs.set("supplier_rank", "1");
          if (activeFilter === "active") qs.set("is_active", "true");
          if (activeFilter === "inactive") qs.set("is_active", "false");
          if (activeFilter === "all") qs.set("is_active", "all");
          const res = await get(`/api/partners?${qs.toString()}`);
          let r = Array.isArray(res?.data) ? res.data : [];
          let filtered = r;
          if (roleFilter === "customer") filtered = filtered.filter((x) => Number(x.customer_rank) > 0);
          if (roleFilter === "supplier") filtered = filtered.filter((x) => Number(x.supplier_rank) > 0);
          all.push(...filtered);
          if (r.length < limit) break;
          pageNo += 1;
        }
        const headers = ["Name","Display Name","Company","Email","Phone","Mobile","City","Type","Customer","Supplier","VAT","Street","ID Number","ID Expiry"];
        const rowsCsv = all.map((p) => [p.name||"", p.display_name||"", p.company_name||p.company||"", p.email||"", p.phone||"", p.mobile||"", p.city||"", p.is_company ? "Company":"Individual", Number(p.customer_rank)>0?"Yes":"No", Number(p.supplier_rank)>0?"Yes":"No", p.vat||"", p.street||"", p.id_number||"", p.id_expiry_date||""]);
        const csv = [headers.join(","), ...rowsCsv.map((row) => row.map((c) => `"${String(c).replace(/"/g,'""')}"`).join(","))].join("\n");
        const blob = new Blob(["\uFEFF"+csv], { type:"text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `clients_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast(`Export CSV: ${all.length} clients`, "success");
      } catch (err) {
        toast(err.message || "Export failed", "error");
      }
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      display_name: p.display_name || "",
      company_name: p.company_name || p.company || "",
      is_company: p.is_company !== false,
      email: p.email || "",
      phone: p.phone || "",
      mobile: p.mobile || "",
      street: p.street || "",
      city: p.city || "",
      vat: p.vat || "",
      customer: Number(p.customer_rank) > 0,
      supplier: Number(p.supplier_rank) > 0,
      is_active: p.is_active !== false,
    });
    setShowForm(true);
  };

  const buildPayload = (f) => ({
    name: f.name.trim(),
    display_name: f.display_name.trim() || undefined,
    company_name: f.company_name.trim() || undefined,
    is_company: !!f.is_company,
    email: f.email.trim() || undefined,
    phone: f.phone.trim() || undefined,
    mobile: f.mobile.trim() || undefined,
    street: f.street.trim() || undefined,
    city: f.city.trim() || undefined,
    vat: f.vat.trim() || undefined,
    customer_rank: f.customer ? 1 : 0,
    supplier_rank: f.supplier ? 1 : 0,
    is_active: !!f.is_active,
  });

  const toggleActive = (e) => {
    const next = e.target.checked;
    if (next === false && form.is_active !== false) {
      const ok = window.confirm("Deactivate this customer? This will fail if the partner has open sales orders.");
      if (!ok) return;
    }
    setForm((f) => ({ ...f, is_active: next }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast("Name is required", "error"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.partners.update(editing.lewoo_uid || editing.id, buildPayload(form));
        toast("Customer updated", "success");
      } else {
        await api.partners.create(buildPayload(form));
        toast("Customer created", "success");
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast(e.message || "Save failed", "error");
    }
    setSaving(false);
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete customer "${p.display_name || p.name}"?`)) return;
    try {
      await api.partners.remove(p.lewoo_uid || p.id);
      toast("Customer deleted", "success");
      if (detail && (detail.id === p.id || detail.lewoo_uid === p.lewoo_uid)) setDetail(null);
      await load();
    } catch (e) {
      toast(e.message || "Delete failed", "error");
    }
  };

  const openDetail = async (p) => {
    setDetail(p);
    setDetailLeads(null);
    setDetailOrders(null);
    const searchName = p.display_name || p.company_name || p.name;
    const [leadsRes, ordersRes] = await Promise.all([
      api.crm.list({ search: searchName, limit: 100 }).catch(() => null),
      api.sales.list({ partner_id: p.id, limit: 10 }).catch(() => null),
    ]);
    const allLeads = rowsOf(leadsRes).rows;
    setDetailLeads(allLeads.filter((l) => l.partner_id === p.id).slice(0, 8));
    setDetailOrders(rowsOf(ordersRes).rows.slice(0, 8));
  };

  const toggleSelect = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  // Envoi direct via infos client — pas de lead requis
  const resolvePartnersToLeadIds = async (partnerIds) => {
    // GARDÉ pour compatibilité si backend n'accepte que lead_ids : mappe sans création
    const rowsById = new Map(rows.map((r) => [r.id, r]));
    const leadIds = [];
    const skipped = [];
    for (const pid of partnerIds) {
      const p = rowsById.get(pid) || rows.find((x) => String(x.id) === String(pid));
      if (!p) { skipped.push({ partner_id: pid, reason: "not_found" }); continue; }
      try {
        const found = await api.crm.list({ search: p.display_name || p.name, limit: 50 }).catch(() => null);
        const candidates = rowsOf(found).rows.filter((l) => String(l.partner_id) === String(pid));
        if (candidates.length > 0) leadIds.push(candidates[0].id);
        else skipped.push({ partner_id: pid, reason: "no_lead" });
      } catch (e) { skipped.push({ partner_id: pid, reason: e.message || "error" }); }
    }
    return { leadIds, skipped };
  };

  const openBulkMail = async () => {
    setMailTemplateId("");
    setMailTemplates([]);
    setMailOpen(true);
    try {
      const res = await get(`/api/mail/templates?limit=100`);
      const n = norm(res);
      const arr = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : [];
      const active = arr.filter((t) => t.is_active !== false && (t.channel_type === "email" || !t.channel_type) && t.model === "crm_lead");
      setMailTemplates(active);
      if (active.length === 1) setMailTemplateId(String(active[0].id));
    } catch (e) { toast(e.message || "Failed to load templates", "error"); }
  };

  const openBulkWhatsApp = async () => {
    setWaTemplateId("");
    setWaTemplates([]);
    setWaOpen(true);
    try {
      const res = await get(`/api/mail/templates?limit=100`);
      const n = norm(res);
      const arr = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : [];
      const active = arr.filter((t) => t.is_active !== false && t.channel_type === "whatsapp" && t.model === "crm_lead");
      setWaTemplates(active);
      if (active.length === 1) setWaTemplateId(String(active[0].id));
    } catch (e) { toast(e.message || "Failed to load templates", "error"); }
  };

  const handleBulkMail = async () => {
    if (!mailTemplateId) { toast("Sélectionne un template", "error"); return; }
    if (selectedIds.length > 250) { toast("Max 250 clients par envoi", "error"); return; }
    setBulkBusy(true);
    try {
      const tpl = mailTemplates.find((t) => String(t.id) === String(mailTemplateId));
      // Envoi direct avec infos client — pas de passage par lead
      let res;
      try {
        res = await post(`/api/mail/bulk`, { template_id: parseInt(mailTemplateId, 10), partner_ids: selectedIds, attach_documents: !!(tpl && tpl.attach_documents) });
      } catch (err) {
        // Fallback legacy : backend n'accepte que lead_ids
        if (!String(err.message).toLowerCase().includes("partner")) throw err;
        const { leadIds, skipped: resolveSkipped } = await resolvePartnersToLeadIds(selectedIds);
        if (leadIds.length === 0) { toast(`Aucun destinataire — ${resolveSkipped.length} ignoré(s) (aucun lead lié) — backend attend partner_ids, à mettre à jour`, "error"); setBulkBusy(false); return; }
        res = await api.mail.bulk({ template_id: parseInt(mailTemplateId, 10), lead_ids: leadIds, attach_documents: !!(tpl && tpl.attach_documents) });
        const queued = Number(res?.queued ?? res?.data?.queued) || 0;
        toast(`${queued} email(s) programmé(s) ${resolveSkipped.length ? `, ${resolveSkipped.length} sans lead ignoré(s)` : ""}`, "success");
        setMailOpen(false); setSelectedIds([]); setBulkBusy(false); return;
      }
      const queued = Number(res?.queued ?? res?.data?.queued ?? res?.data) || Number(res?.data?.queued) || 0;
      const skipped = res?.skipped || res?.data?.skipped || [];
      const inactives = selectedIds.map((id) => rows.find((r) => String(r.id) === String(id))).filter((r) => r && r.is_active === false);
      if (queued > 0 && inactives.length > 0) {
        const names = inactives.map((r) => r.display_name || r.name).slice(0, 3).join(", ");
        if (window.confirm(`${inactives.length} client(s) relancé(s) sont inactifs (${names}${inactives.length > 3 ? "…" : ""}) — les activer maintenant ?`)) {
          try { await Promise.all(inactives.map((r) => api.partners.update(r.lewoo_uid || r.id, { is_active: true }))); await load(); } catch (e) { toast(e.message || "Activation échouée", "error"); }
        }
      }
      // Relance part toujours, toast success final comme demandé
      setMailOpen(false); setSelectedIds([]);
      toast(`${queued} email(s) programmé(s)${skipped.length ? `, ${skipped.length} ignoré(s) (sans email)` : ""}`, queued ? "success" : "info");
    } catch (e) { toast(e.message || "Bulk email failed", "error"); }
    setBulkBusy(false);
  };

  const handleBulkWhatsApp = async () => {
    if (!waTemplateId) { toast("Sélectionne un template WhatsApp", "error"); return; }
    if (selectedIds.length > 250) { toast("Max 250 clients par envoi", "error"); return; }
    setBulkBusy(true);
    try {
      let res;
      try {
        res = await post(`/api/mail/bulk`, { template_id: parseInt(waTemplateId, 10), partner_ids: selectedIds, channel: "whatsapp" });
      } catch (err) {
        if (!String(err.message).toLowerCase().includes("partner")) throw err;
        const { leadIds, skipped: resolveSkipped } = await resolvePartnersToLeadIds(selectedIds);
        if (leadIds.length === 0) { toast(`Aucun destinataire — ${resolveSkipped.length} ignoré(s) — backend attend partner_ids`, "error"); setBulkBusy(false); return; }
        res = await api.mail.bulk({ template_id: parseInt(waTemplateId, 10), lead_ids: leadIds, channel: "whatsapp" });
        const queued = Number(res?.queued ?? res?.data?.queued) || 0;
        toast(`${queued} WhatsApp programmé(s)`, "success");
        setWaOpen(false); setSelectedIds([]); setBulkBusy(false); return;
      }
      const queued = Number(res?.queued ?? res?.data?.queued ?? res?.data) || 0;
      const skipped = res?.skipped || res?.data?.skipped || [];
      const inactives = selectedIds.map((id) => rows.find((r) => String(r.id) === String(id))).filter((r) => r && r.is_active === false);
      if (queued > 0 && inactives.length > 0) {
        const names = inactives.map((r) => r.display_name || r.name).slice(0, 3).join(", ");
        if (window.confirm(`${inactives.length} client(s) relancé(s) sont inactifs (${names}${inactives.length > 3 ? "…" : ""}) — les activer maintenant ?`)) {
          try { await Promise.all(inactives.map((r) => api.partners.update(r.lewoo_uid || r.id, { is_active: true }))); await load(); } catch (e) { toast(e.message || "Activation échouée", "error"); }
        }
      }
      setWaOpen(false); setSelectedIds([]);
      toast(`${queued} WhatsApp programmé(s)${skipped.length ? `, ${skipped.length} ignoré(s) (sans téléphone)` : ""}`, queued ? "success" : "info");
    } catch (e) { toast(e.message || "Bulk WhatsApp failed", "error"); }
    setBulkBusy(false);
  };

  if (!loaded) return <LoadingScreen />;

  if (detail) {
    const detailName = detail.display_name || detail.name || `Customer ${detail.id}`;
    const initials = detailName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
    return (
      <div style={{ minHeight: "100vh", background: "#f6f7f8", color: "#263746", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif' }}>
        <div style={{ minHeight: 32, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 10px", background: "#fff", borderBottom: "1px solid #d8dde2", fontSize: 12 }}>
          <div style={{ display: "flex", gap: 7, alignItems: "center", color: "#63758a" }}><button type="button" onClick={() => setDetail(null)} style={{ border: 0, background: "transparent", color: "#63758a", cursor: "pointer", padding: 0 }}>Customers</button><span>/</span><strong>{detailName}</strong></div>
          <div style={{ display: "flex", gap: 13, alignItems: "center", color: "#536879", fontSize: 11 }}><button type="button" style={{ ...iconBtn("#536879"), border: 0 }}><FileText size={13} /> Print</button><button type="button" style={{ ...iconBtn("#536879"), border: 0 }}>⚙ Action</button><span>13 / 80</span><span>‹</span><span>›</span><button type="button" onClick={openNew} style={{ ...stylesOdooButton, background: "#6594ad", color: "#fff" }}>New</button></div>
        </div>
        <div style={{ width: "min(100%, 1000px)", margin: "0 auto", background: "#fff", border: "1px solid #dfe4e7", marginTop: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid #dfe4e7" }}>
            {[ ["Meetings", "0", Clock], ["Opportunities", String(detailLeads?.length || 0), FileText], ["Send", "", MessageCircle], ["Sales", String(detailOrders?.length || 0), ShoppingCart], ["Purchases", "0", FileText], ["On-Time Rate", "No Data Yet", Clock], ["Invoiced", "0 FCFA", FileText] ].map(([label, value, Icon]) => <div key={label} style={{ display: "flex", gap: 8, alignItems: "center", minHeight: 45, padding: "0 10px", borderRight: "1px solid #dfe4e7", fontSize: 10, color: "#63758a" }}><Icon size={16} color="#536879" /><span><strong style={{ display: "block", color: "#536879", fontSize: 11 }}>{value}</strong>{label}</span></div>)}
          </div>
          <div style={{ padding: "18px 20px 0" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 13, fontSize: 11 }}><label><input type="radio" checked={detail.is_company === false} readOnly /> Individual</label><label><input type="radio" checked={detail.is_company !== false} readOnly /> Company</label></div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #84919a", paddingBottom: 5 }}><h1 style={{ margin: 0, fontSize: 20, fontWeight: 400 }}>{detailName}</h1><div style={{ width: 66, height: 66, display: "flex", alignItems: "center", justifyContent: "center", background: detail.image_url ? `url(${detail.image_url}) center / cover` : "#d9dde0", color: "#fff", fontSize: 20, fontWeight: 600 }}>{detail.image_url ? null : initials}</div></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 24, marginTop: 17 }}>
              <div><OdooDetailField label="Address">{[detail.street, detail.city, detail.state, detail.country].filter(Boolean).join(", ") || ""}</OdooDetailField><OdooDetailField label="Tax ID ?">{detail.vat}</OdooDetailField><OdooDetailField label="RFID code">{detail.rfid_code}</OdooDetailField><OdooDetailField label="Person Code">{detail.person_code}</OdooDetailField><OdooDetailField label="CNI">{detail.cni}</OdooDetailField><OdooDetailField label="External ID Dev Support">{detail.external_id || "0"}</OdooDetailField></div>
              <div><OdooDetailField label="Phone">{detail.phone}</OdooDetailField><OdooDetailField label="Mobile">{detail.mobile}</OdooDetailField><OdooDetailField label="ChatRoom">{detail.chatroom}</OdooDetailField><OdooDetailField label="Email">{detail.email}</OdooDetailField><OdooDetailField label="Website">{detail.website}</OdooDetailField><OdooDetailField label="Customer Rank">{detail.customer_rank || "0"}</OdooDetailField><OdooDetailField label="Language">{detail.lang || "English (US)"}</OdooDetailField><OdooDetailField label="Customer code">{detail.ref || detail.lewoo_uid}</OdooDetailField></div>
            </div>
            <div style={{ display: "flex", gap: 18, marginTop: 18, borderBottom: "1px solid #dfe4e7", fontSize: 10.5, color: "#6f668d" }}>{["Contacts & Addresses", "Sales & Purchase", "Service Info", "Invoicing", "Internal Notes", "Tracking Info"].map((tab, index) => <span key={tab} style={{ padding: "8px 5px", border: index === 0 ? "1px solid #dfe4e7" : 0, borderBottom: index === 0 ? "1px solid #fff" : 0, marginBottom: -1 }}>{tab}</span>)}</div>
            <div style={{ minHeight: 70, padding: "20px 8px", color: "#6f668d", fontSize: 11 }}>ADD</div>
          </div>
        </div>
        <div style={{ width: "min(100%, 1000px)", margin: "10px auto 0", background: "#fff", borderTop: "1px solid #dfe4e7" }}><div style={{ display: "flex", gap: 0, borderBottom: "1px solid #dfe4e7" }}><button type="button" style={{ background: "#263d4a", color: "#fff", border: 0, padding: "8px 13px", fontSize: 11 }}>Send message</button><button type="button" style={chatterTabStyle}>Log note</button><button type="button" style={chatterTabStyle}>◷ Activities</button><span style={{ marginLeft: "auto", padding: "8px 13px", fontSize: 11, color: "#63758a" }}>♧ 1 &nbsp; Follow</span></div><div style={{ minHeight: 90, padding: "15px", fontSize: 11, color: "#63758a" }}><strong>Today</strong><div style={{ marginTop: 18, paddingTop: 10, borderTop: "1px solid #edf0f2" }}>Customer record opened</div></div></div>
        {canManage && <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "14px" }}><button onClick={() => { setDetail(null); openEdit(detail); }} className="ess-btn ess-btn-secondary"><Pencil size={14} /> Edit</button><button onClick={() => setDetail(null)} className="ess-btn ess-btn-secondary">Back to Customers</button></div>}
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f6f7f8", color: "#263746", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif' }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 31, padding: "0 10px", background: "#fff", borderBottom: "1px solid #d8dde2" }}><h1 style={{ margin: 0, fontSize: 15, fontWeight: 400, color: "#63758a" }}>Customers</h1><span style={{ fontSize: 11, color: "#63758a" }}>Search...</span></div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 38, padding: "0 10px", background: "#fff", borderBottom: "1px solid #d8dde2" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {canManage && (
            <button
              onClick={openNew}
              style={{
                ...stylesOdooButton,
                background: "#6594ad",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                lineHeight: 1,
              }}
            >
              <Plus size={13} />
              <span>NEW</span>
            </button>
          )}
          {selectedIds.length > 0 && canManage && <><button onClick={openBulkMail} style={stylesOdooButton}>Email ({selectedIds.length})</button><button onClick={openBulkWhatsApp} style={stylesOdooButton}>WhatsApp ({selectedIds.length})</button></>}
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center", fontSize: 11, color: "#536879" }}><span>⚱ Filters</span><span>▦ Group By</span><span>★ Favorites</span><span>⟳</span><span>1-{rows.length} / {total}</span><span>‹</span><span>›</span><span>▦</span><span>☷</span></div>
      </div>
      <div style={{ padding: "8px 10px", background: "#fff", borderBottom: "1px solid #d8dde2" }}>
        <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}><Search size={13} style={{ position: "absolute", left: 8, top: 8, color: "#8a98a3" }} /><input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, email..." style={{ ...inputStyle, height: 28, borderRadius: 2, padding: "4px 8px 4px 27px", fontSize: 11 }} /></div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ ...inputStyle, width: 120, height: 28, borderRadius: 2, padding: "3px 7px", fontSize: 11 }}><option value="all">All types</option><option value="company">Companies</option><option value="individual">Individuals</option></select>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={{ ...inputStyle, width: 120, height: 28, borderRadius: 2, padding: "3px 7px", fontSize: 11 }}><option value="all">All roles</option><option value="customer">Customers</option><option value="supplier">Suppliers</option></select>
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} style={{ ...inputStyle, width: 120, height: 28, borderRadius: 2, padding: "3px 7px", fontSize: 11 }}><option value="all">All status</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
          <button onClick={() => handleExport("xlsx")} style={{ ...stylesOdooButton, height: 28 }}><Download size={13} /> Export</button>
        </div>
      </div>
      <div style={{ padding: "0 10px 10px" }}>

        {loading ? (
          <LoadingScreen />
        ) : rows.length === 0 ? (
          <EmptyState icon={Building2} title="No customers found" subtitle={canManage ? "Create your first customer to link it to leads and quotations" : "Adjust your filters"} />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(235px, 1fr))", gap: 6, padding: "10px", background: "#f4f5f6" }}>
            {rows.map((p) => {
              const selected = selectedIds.includes(p.id);
              const customerName = p.display_name || p.name || `Customer ${p.id}`;
              const initials = customerName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
              return (
                <article key={p.id ?? p.lewoo_uid} style={{ minHeight: 104, display: "grid", gridTemplateColumns: "72px 1fr", gap: 10, padding: 8, boxSizing: "border-box", border: `1px solid ${selected ? "#6594ad" : "#d8dde2"}`, background: "#fff", position: "relative" }}>
                  <button type="button" onClick={() => openDetail(p)} aria-label={`Open ${customerName}`} style={{ width: 72, height: 72, border: 0, padding: 0, background: p.image_url ? `url(${p.image_url}) center / cover` : "#d9dde0", color: "#fff", fontSize: 22, fontWeight: 600, cursor: "pointer" }}>{p.image_url ? null : initials}</button>
                  <div style={{ minWidth: 0, paddingRight: 4 }}>
                    <button type="button" onClick={() => openDetail(p)} style={{ display: "block", width: "100%", overflow: "hidden", border: 0, padding: 0, background: "transparent", color: "#263746", fontSize: 12, fontWeight: 600, textAlign: "left", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>{customerName}</button>
                    <div style={{ marginTop: 5, color: "#63758a", fontSize: 10 }}>{p.city || "Cameroon"}</div>
                    <div style={{ overflow: "hidden", marginTop: 4, color: "#63758a", fontSize: 10, textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.email || p.phone || "No contact information"}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9, color: "#6f668d", fontSize: 10 }}><span>$ {p.customer_rank || 1}</span>{Number(p.supplier_rank) > 0 && <span>★ {p.supplier_rank}</span>}<span style={{ marginLeft: "auto" }}><Clock size={13} /></span></div>
                  </div>
                  <div style={{ position: "absolute", top: 6, left: 6 }}>{canManage && <input type="checkbox" checked={selected} onChange={() => toggleSelect(p.id)} aria-label={`Select ${customerName}`} />}</div>
                  <div style={{ position: "absolute", right: 5, bottom: 5, display: "flex", gap: 3 }}><button title="View" onClick={() => openDetail(p)} style={iconBtn("#225f2b")}><Eye size={12} /></button>{canManage && <button title="Edit" onClick={() => openEdit(p)} style={iconBtn("#b45309")}><Pencil size={12} /></button>}</div>
                </article>
              );
            })}
          </div>
        )}

        {(totalPages > 1 || page > 1) && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderTop: "1px solid #e2e7e2", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>
              {knownTotal ? (
                <span>{total} customers · page {page} / {totalPages}</span>
              ) : (
                <span>page {page}</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ padding: "6px 14px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1, border: "1px solid #e2e7e2", background: "#fff", color: "#475569", transition: "all 0.15s" }}
                onMouseEnter={(e) => { if (page > 1) { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#cbd5e1"; } }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e7e2"; }}
              >
                ‹ Previous
              </button>
              {pageNumbers.map((n) =>
                n === "…" ? (
                  <span key={`ellipsis-${n}`} style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8", padding: "0 4px", userSelect: "none" }}>…</span>
                ) : (
                  <button
                    key={n}
                    disabled={n === page}
                    onClick={() => setPage(n)}
                    style={{
                      minWidth: 34, height: 34, padding: "0 8px", borderRadius: 6, fontSize: 13, fontWeight: n === page ? 600 : 500,
                      cursor: n === page ? "default" : "pointer", opacity: n === page ? 1 : 0.7,
                      border: n === page ? "1px solid #225f2b" : "1px solid transparent",
                      background: n === page ? "#225f2b" : "transparent",
                      color: n === page ? "#fff" : "#475569",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { if (n !== page) { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.opacity = "1"; } }}
                    onMouseLeave={(e) => { if (n !== page) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.opacity = "0.7"; } }}
                  >
                    {n}
                  </button>
                )
              )}
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ padding: "6px 14px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.4 : 1, border: "1px solid #e2e7e2", background: "#fff", color: "#475569", transition: "all 0.15s" }}
                onMouseEnter={(e) => { if (page < totalPages) { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#cbd5e1"; } }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e7e2"; }}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit Customer — ${editing.display_name || editing.name}` : "New Customer"} size="lg">
        <div>
          <FormField label="Name" required>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Acme SARL or John Doe" style={inputStyle} />
          </FormField>
          <div style={{ display: "grid", gap: "14px", gridTemplateColumns: "1fr 1fr" }} className="ess-rg-2">
            <FormField label="Type">
              <select value={form.is_company ? "company" : "individual"} onChange={(e) => setForm((f) => ({ ...f, is_company: e.target.value === "company" }))} style={inputStyle}>
                <option value="company">Company</option>
                <option value="individual">Individual</option>
              </select>
            </FormField>
            <FormField label="Company Name">
              <input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} placeholder="Legal name" style={inputStyle} />
            </FormField>
            <FormField label="Email">
              <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="contact@acme.com" style={inputStyle} />
            </FormField>
            <FormField label="Phone">
              <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+237 …" style={inputStyle} />
            </FormField>
            <FormField label="Mobile">
              <input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} style={inputStyle} />
            </FormField>
            <FormField label="VAT">
              <input value={form.vat} onChange={(e) => setForm((f) => ({ ...f, vat: e.target.value }))} style={inputStyle} />
            </FormField>
            <FormField label="Street">
              <input value={form.street} onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))} style={inputStyle} />
            </FormField>
            <FormField label="City">
              <input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} style={inputStyle} />
            </FormField>
          </div>
          <FormField label="Display Name">
            <input value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} placeholder="Defaults to name" style={inputStyle} />
          </FormField>
          <FormField label="Roles">
            <div style={{ display: "flex", gap: 18 }}>
              <label style={{ fontSize: 13.5, color: "#334155", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={form.customer} onChange={(e) => setForm((f) => ({ ...f, customer: e.target.checked }))} /> Customer
              </label>
              <label style={{ fontSize: 13.5, color: "#334155", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.checked }))} /> Supplier
              </label>
            </div>
          </FormField>
          <FormField label="Status">
            <label style={{ fontSize: 13.5, color: "#334155", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_active} onChange={toggleActive} /> Active
            </label>
          </FormField>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 20 }}>
            <button onClick={() => setShowForm(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button disabled={saving} onClick={submit} className="ess-btn ess-btn-primary">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? detail.display_name || detail.name || `Customer ${detail.id}` : ""} size="lg">
        {detail && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <DetailField label="Type">{detail.is_company !== false ? "Company" : "Individual"}</DetailField>
              <DetailField label="Company Name">{detail.company_name || detail.company || "—"}</DetailField>
              <DetailField label="Email">{detail.email || "—"}</DetailField>
              <DetailField label="Phone">{[detail.phone, detail.mobile].filter(Boolean).join(" · ") || "—"}</DetailField>
              <DetailField label="Address" full>{[detail.street, detail.city].filter(Boolean).join(", ") || "—"}</DetailField>
              <DetailField label="VAT">{detail.vat || "—"}</DetailField>
              <DetailField label="Roles">
                <div style={{ display: "flex", gap: 4 }}>
                  {Number(detail.customer_rank) > 0 && chip("Customer", "#f0f7f0", "#225f2b")}
                  {Number(detail.supplier_rank) > 0 && chip("Supplier", "#fffbeb", "#b45309")}
                  {Number(detail.customer_rank) === 0 && Number(detail.supplier_rank) === 0 && "—"}
                </div>
              </DetailField>
              <DetailField label="Status">{detail.is_active !== false ? chip("Active", "#f0f7f0", "#225f2b") : chip("Inactive", "#fff1f2", "#be123c")}</DetailField>
            </div>

            <div style={{ marginTop: 22 }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
                <FileText size={15} /> Related Leads
              </h4>
              <div style={{ marginTop: 10 }}>
                {detailLeads == null ? (
                  <div style={{ fontSize: 12.5, color: "#94a3b8" }}>Loading…</div>
                ) : detailLeads.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: "#94a3b8" }}>No leads linked to this customer.</div>
                ) : (
                  detailLeads.map((l) => (
                    <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid #f1f3f0", borderRadius: 8, marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                      <span style={{ fontSize: 11.5, color: "#64748b" }}>{l.stage_name || "—"}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#225f2b" }}>{l.expected_revenue != null ? fmt.money(l.expected_revenue) : ""}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div style={{ marginTop: 22 }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
                <ShoppingCart size={15} /> Sales Orders
              </h4>
              <div style={{ marginTop: 10 }}>
                {detailOrders == null ? (
                  <div style={{ fontSize: 12.5, color: "#94a3b8" }}>Loading…</div>
                ) : detailOrders.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: "#94a3b8" }}>No sales orders for this customer.</div>
                ) : (
                  detailOrders.map((o) => (
                    <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid #f1f3f0", borderRadius: 8, marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{o.name}</span>
                      <span style={stateBadgeStyleObj(o.state)}>{o.state}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#225f2b", marginLeft: "auto" }}>{o.amount_total != null ? fmt.money(o.amount_total) : ""}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {canManage && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 22, flexWrap: "wrap" }}>
                <button onClick={async () => {
                  setQuoteOpen(true); setQuoteLines([]); setQuoteProducts([]); setQuoteLoading(true);
                  try { const res = await api.products.list({ limit: 500, active: true }); const n = norm(res); const arr = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : []; setQuoteProducts(arr); if (arr.length > 0) setQuoteLines([{ product_id: String(arr[0].id), product_uom_qty: 1, price_unit: arr[0].list_price || 0, name: arr[0].name }]); } catch { setQuoteProducts([]); } setQuoteLoading(false);
                }} className="ess-btn ess-btn-primary"><FileText size={14} /> Générer devis</button>
                <button onClick={() => { setDetail(null); openEdit(detail); }} className="ess-btn ess-btn-secondary"><Pencil size={14} /> Edit</button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={mailOpen} onClose={() => setMailOpen(false)} title={`Envoyer Email — ${selectedIds.length} client(s)`} size="lg">
        <div>
          <div style={{ marginBottom: 12, fontSize: 12.5, color: "#64748b" }}>Même système que relance CRM `AdminPanelCrmPage.js:790` → templates `model=crm_lead`. Seuls les clients déjà liés à une opportunité/lead (`partner_id`) sont envoyables — les autres apparaissent dans `ignorés (no_lead)`.</div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Template email *</label>
            {mailTemplates.length === 0 ? <div style={{ fontSize: 12, color: "#94a3b8" }}>Aucun template email crm_lead — crée-en dans CRM Settings → Templates</div> : <select value={mailTemplateId} onChange={(e) => setMailTemplateId(e.target.value)} style={inputStyle}><option value="">Choisir…</option>{mailTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>}
          </div>
          {(() => { const tpl = mailTemplates.find((t) => String(t.id) === String(mailTemplateId)); if (!tpl) return null; return <div style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 12, background: "#fafbfa", fontSize: 13 }}><div style={{ fontWeight: 600, marginBottom: 6 }}>Sujet: {tpl.subject || "—"}</div><div dangerouslySetInnerHTML={{ __html: tpl.body_html || "" }} style={{ maxHeight: 160, overflowY: "auto" }} /></div>; })()}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setMailOpen(false)} className="ess-btn ess-btn-secondary">Annuler</button>
            <button disabled={bulkBusy || !mailTemplateId} onClick={handleBulkMail} className="ess-btn ess-btn-primary">{bulkBusy ? "Envoi…" : `Envoyer à ${selectedIds.length}`}</button>
          </div>
        </div>
      </Modal>

      <Modal open={waOpen} onClose={() => setWaOpen(false)} title={`Envoyer WhatsApp — ${selectedIds.length} client(s)`} size="lg">
        <div>
          <div style={{ marginBottom: 12, fontSize: 12.5, color: "#64748b" }}>Même système que relance CRM → templates `channel_type=whatsapp`. Variables `{"{{lead.name}}"}`, `{"{{lead.phone}}"}`, `{"{{lead.company}}"}`. Livraison via `mail_outbox` `source=bulk`.</div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Template WhatsApp *</label>
            {waTemplates.length === 0 ? <div style={{ fontSize: 12, color: "#94a3b8" }}>Aucun template WhatsApp — crée-en dans CRM Settings → Templates (canal WhatsApp)</div> : <select value={waTemplateId} onChange={(e) => setWaTemplateId(e.target.value)} style={inputStyle}><option value="">Choisir…</option>{waTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>}
          </div>
          {(() => { const tpl = waTemplates.find((t) => String(t.id) === String(waTemplateId)); if (!tpl) return null; return <div style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 12, background: "#f0fdf4", fontSize: 13, whiteSpace: "pre-wrap" }}>{tpl.body_text || "—"}</div>; })()}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16, marginTop: 16 }}>
            <button onClick={() => setWaOpen(false)} className="ess-btn ess-btn-secondary">Annuler</button>
            <button disabled={bulkBusy || !waTemplateId} onClick={handleBulkWhatsApp} className="ess-btn ess-btn-primary" style={{ background: "#166534" }}>{bulkBusy ? "Envoi…" : `Envoyer à ${selectedIds.length}`}</button>
          </div>
        </div>
      </Modal>

      <Modal open={quoteOpen} onClose={() => setQuoteOpen(false)} title={detail ? `Générer devis — ${detail.display_name || detail.name}` : "Générer devis"} size="lg">
        <div>
          {quoteLoading ? <LoadingScreen /> : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: "#475569" }}>Affecte les produits du catalogue au client</span>
                <button onClick={() => setQuoteLines((l) => [...l, { product_id: quoteProducts[0] ? String(quoteProducts[0].id) : "", product_uom_qty: 1, price_unit: quoteProducts[0]?.list_price || 0, name: quoteProducts[0]?.name || "" }])} className="ess-btn ess-btn-secondary" style={{ padding: "6px 10px" }}><Plus size={14} /> Ajouter ligne</button>
              </div>
              {quoteLines.length === 0 && <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: "#94a3b8", border: "1px dashed #cbd5e1", borderRadius: 8 }}>Aucune ligne — ajoute un produit</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {quoteLines.map((ln, idx) => {
                  const prod = quoteProducts.find((p) => String(p.id) === String(ln.product_id));
                  return (
                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", background: "#f8fafc", border: "1px solid #e2e7e2", borderRadius: 8, padding: 10, flexWrap: "wrap" }}>
                      <select value={ln.product_id} onChange={(e) => {
                        const p = quoteProducts.find((x) => String(x.id) === String(e.target.value));
                        setQuoteLines((prev) => prev.map((r, i) => i === idx ? { ...r, product_id: e.target.value, price_unit: p?.list_price || r.price_unit, name: p?.name || r.name } : r));
                      }} style={{ ...inputStyle, flex: "2 1 200px", background: "#fff" }}>
                        <option value="">Choisir produit…</option>
                        {quoteProducts.map((p) => <option key={p.id} value={p.id}>{p.name}{p.default_code ? ` (${p.default_code})` : ""} — ${fmt.money(p.list_price)}</option>)}
                      </select>
                      <input type="number" min="1" value={ln.product_uom_qty} onChange={(e) => setQuoteLines((prev) => prev.map((r, i) => i === idx ? { ...r, product_uom_qty: e.target.value } : r))} placeholder="Qté" style={{ ...inputStyle, width: 80, background: "#fff" }} />
                      <input type="number" min="0" value={ln.price_unit} onChange={(e) => setQuoteLines((prev) => prev.map((r, i) => i === idx ? { ...r, price_unit: e.target.value } : r))} placeholder="PU" style={{ ...inputStyle, width: 120, background: "#fff" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", minWidth: 90, textAlign: "right" }}>{fmt.money((Number(ln.product_uom_qty) || 0) * (Number(ln.price_unit) || 0))}</span>
                      <button onClick={() => setQuoteLines((prev) => prev.filter((_, i) => i !== idx))} style={{ border: "none", background: "transparent", color: "#be123c", cursor: "pointer", padding: 6 }}><Trash2 size={16} /></button>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f0f7f0", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: "#475569" }}>Total ({quoteLines.length} lignes)</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>{fmt.money(quoteLines.reduce((s, l) => s + (Number(l.product_uom_qty) || 0) * (Number(l.price_unit) || 0), 0))}</span>
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: 16 }}>
                <button onClick={() => setQuoteOpen(false)} className="ess-btn ess-btn-secondary">Annuler</button>
                <button disabled={quoteBusy || quoteLines.length === 0 || quoteLines.some((l) => !l.product_id)} onClick={async () => {
                  if (!detail) return; setQuoteBusy(true);
                  try {
                    const lines = quoteLines.filter((l) => l.product_id).map((l) => ({ product_id: parseInt(l.product_id), product_uom_qty: Number(l.product_uom_qty) || 1, price_unit: Number(l.price_unit) || 0, name: quoteProducts.find((p) => String(p.id) === String(l.product_id))?.name || l.name }));
                    const order = await api.sales.create({ partner_id: detail.id, state: "draft", lines });
                    toast(`Devis ${order?.name || ""} créé`, "success");
                    setQuoteOpen(false); setDetail(null); window.location.href = "/admin-panel/sales";
                  } catch (e) { toast(e.message || "Création devis échouée", "error"); }
                  setQuoteBusy(false);
                }} className="ess-btn ess-btn-primary">{quoteBusy ? "Création…" : "Créer le devis"}</button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelPartnersPage;
